import { useEffect, useState } from 'react';
import {
  BookOpen,
  CheckCircle2,
  Download,
  ExternalLink,
  ImagePlus,
  LoaderCircle,
  MonitorSmartphone,
  Save,
  Shield,
  Sparkles,
  Upload,
} from 'lucide-react';
import { api } from '../lib/api';

type Item={level:'pass'|'warning'|'blocker'|'info';code:string;title:string;detail:string};
type Bundle={
  rulesVersion:string;
  profile:{
    digitalRevision:number;
    sourceMetadataRevision:number;
    readingDirection:'ltr'|'rtl';
    facingPages:boolean;
    drmMode:'drm'|'drm-free';
    coverMode:'upload'|'kdp-cover-creator';
    coverUrl?:string|null;
    coverWidthPx?:number|null;
    coverHeightPx?:number|null;
    coverBytes:number;
    includeCommercialPages:boolean;
    toc:Array<{title:string;pageNumber:number}>;
    guidedView:Array<{
      pageId:string;
      pageNumber:number;
      purpose?:string;
      selections:Array<{order:number;xPct:number;yPct:number;widthPct:number;heightPct:number;sourcePanelIndex:number}>;
    }>;
    guidedViewReviewed:boolean;
    kindleCreatePreviewConfirmed:boolean;
    kpfExportConfirmed:boolean;
    metadataMatchConfirmed:boolean;
    digitalPdfUrl?:string|null;
    packageUrl?:string|null;
    digitalAsin?:string;
    publishedSnapshot?:Record<string,unknown>|null;
    publishedAt?:string|null;
  };
  validation:{
    status:string;blockers:number;warnings:number;passes:number;sourcePages:number;
    items:Item[];
    metadataForLinking:Record<string,unknown>;
    note:string;
  };
  history:Array<{id:string;kind:string;url?:string|null;bytes:number;digitalRevision:number;createdAt:string}>;
  links:Record<string,string>;
};

export default function KindleDigitalEdition({projectId}:{projectId:string}){
  const[bundle,setBundle]=useState<Bundle|null>(null);
  const[form,setForm]=useState<Bundle['profile']|null>(null);
  const[busy,setBusy]=useState('');
  const[message,setMessage]=useState('');

  const load=async()=>{
    const{data}=await api.get<Bundle>(`/api/projects/${projectId}/kindle`);
    setBundle(data);setForm(data.profile);
  };
  useEffect(()=>{load().catch(e=>setMessage(e instanceof Error?e.message:'Kindle edition could not be loaded.'));},[projectId]);

  const save=async()=>{
    if(!form)return;
    setBusy('save');setMessage('');
    try{
      await api.put(`/api/projects/${projectId}/kindle/profile`,form);
      await load();setMessage('Digital-edition settings saved.');
    }catch(e){setMessage(e instanceof Error?e.message:'Digital settings could not be saved.')}
    finally{setBusy('')}
  };

  const guided=async()=>{
    setBusy('guided');setMessage('');
    try{
      await api.post(`/api/projects/${projectId}/kindle/guided-view/suggest`);
      await load();setMessage('Guided View panel-order suggestions rebuilt from the current comic layouts.');
    }catch(e){setMessage(e instanceof Error?e.message:'Guided View suggestions could not be created.')}
    finally{setBusy('')}
  };

  const toc=async()=>{
    setBusy('toc');setMessage('');
    try{
      await api.post(`/api/projects/${projectId}/kindle/toc/suggest`);
      await load();setMessage('Table-of-contents suggestions rebuilt.');
    }catch(e){setMessage(e instanceof Error?e.message:'TOC suggestions could not be created.')}
    finally{setBusy('')}
  };

  const uploadCover=async(file:File|null)=>{
    if(!file)return;
    setBusy('cover');setMessage('');
    try{
      const fd=new FormData();fd.append('file',file);
      await api.postForm(`/api/projects/${projectId}/kindle/cover`,fd);
      await load();setMessage('Kindle marketing cover uploaded.');
    }catch(e){setMessage(e instanceof Error?e.message:'Cover upload failed.')}
    finally{setBusy('')}
  };

  const build=async()=>{
    setBusy('build');setMessage('');
    try{
      await save();
      const{data}=await api.post<{url:string;bytes:number;sourcePdfUrl:string}>(`/api/projects/${projectId}/kindle/package`);
      await load();
      setMessage(`Kindle Create package built (${(data.bytes/1_000_000).toFixed(1)} MB).`);
      window.open(data.url,'_blank','noopener,noreferrer');
    }catch(e){setMessage(e instanceof Error?e.message:'Kindle Create package could not be built.')}
    finally{setBusy('')}
  };

  const published=async()=>{
    const asin=prompt('Kindle ASIN (optional but recommended after publication):',form?.digitalAsin||'');
    if(asin===null)return;
    setBusy('published');
    try{
      await api.post(`/api/projects/${projectId}/kindle/published-snapshot`,{asin});
      await load();setMessage('Kindle publication snapshot recorded.');
    }catch(e){setMessage(e instanceof Error?e.message:'Publication snapshot could not be recorded.')}
    finally{setBusy('')}
  };

  if(!bundle||!form)return <section className="card p-6"><LoaderCircle className="spin"/><b className="ml-2">Loading digital edition…</b></section>;

  return <div className="space-y-5">
    <section className="card p-6">
      <div className="row items-start">
        <div><span className="eyebrow">KINDLE & DIGITAL EDITION</span><h2>Derive digital. Protect the print master.</h2><p className="muted">Kindle Create source package · Guided View plan · DRM decision · edition linking.</p></div>
        <button className="primary" disabled={!!busy} onClick={save}><Save size={15}/>{busy==='save'?'Saving…':'Save'}</button>
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mt-6">
        <div className="card p-5">
          <h3>Reading experience</h3>
          <div className="formGrid mt-4">
            <Field label="Reading direction"><select value={form.readingDirection} onChange={e=>setForm({...form,readingDirection:e.target.value as 'ltr'|'rtl'})}><option value="ltr">Left → Right</option><option value="rtl">Right → Left / Manga</option></select></Field>
            <Field label="Facing pages"><select value={form.facingPages?'yes':'no'} onChange={e=>setForm({...form,facingPages:e.target.value==='yes'})}><option value="no">Single pages</option><option value="yes">Enable facing pages</option></select></Field>
          </div>
          <Toggle label="Include commercial pages in digital source" checked={form.includeCommercialPages} onChange={v=>setForm({...form,includeCommercialPages:v})}/>
          <div className="flex gap-2 flex-wrap mt-4">
            <button className="secondary" disabled={!!busy} onClick={guided}>{busy==='guided'?<LoaderCircle className="spin" size={14}/>:<Sparkles size={14}/>}Build Guided View plan</button>
            <button className="secondary" disabled={!!busy} onClick={toc}>{busy==='toc'?<LoaderCircle className="spin" size={14}/>:<BookOpen size={14}/>}Build TOC suggestions</button>
          </div>
          <div className="specGrid mt-4">
            <div className="softBox"><b>Guided View</b><strong>{form.guidedView.length} pages</strong><small>{form.guidedView.reduce((n,p)=>n+p.selections.length,0)} selections</small></div>
            <div className="softBox"><b>TOC</b><strong>{form.toc.length}</strong><small>suggested entries</small></div>
          </div>
        </div>

        <div className="card p-5">
          <h3>DRM</h3>
          <p className="muted text-sm mt-2">This choice is made again in KDP. The app records your intended setting so the release checklist stays consistent.</p>
          <div className="grid gap-3 mt-4">
            <button className={`rounded-2xl border p-4 text-left ${form.drmMode==='drm'?'border-[#ef5b3e] bg-[#fff0eb]':'border-black/10 bg-white'}`} onClick={()=>setForm({...form,drmMode:'drm'})}><Shield size={18}/><b className="block mt-2">Apply DRM</b><span className="muted text-sm">Limits purchased content to Kindle apps/devices under Amazon’s current policy.</span></button>
            <button className={`rounded-2xl border p-4 text-left ${form.drmMode==='drm-free'?'border-[#ef5b3e] bg-[#fff0eb]':'border-black/10 bg-white'}`} onClick={()=>setForm({...form,drmMode:'drm-free'})}><Download size={18}/><b className="block mt-2">DRM-free</b><span className="muted text-sm">Verified purchasers can download confirmed DRM-free books as EPUB/PDF under Amazon’s current policy.</span></button>
          </div>
          <a className="secondary mt-4" href={bundle.links.drmGuide} target="_blank"><ExternalLink size={14}/>Current KDP DRM guidance</a>
        </div>
      </div>

      <div className="card p-5 mt-5">
        <div className="row items-start"><div><h3>Kindle marketing cover</h3><p className="muted text-sm">Separate from the print wrap. Upload a clean front-cover JPEG or finish it in KDP Cover Creator.</p></div>{form.coverUrl&&<img src={form.coverUrl} className="h-32 w-20 object-cover rounded-lg border"/>}</div>
        <div className="flex gap-3 flex-wrap mt-4">
          <button className={form.coverMode==='upload'?'primary':'secondary'} onClick={()=>setForm({...form,coverMode:'upload'})}>Upload cover</button>
          <button className={form.coverMode==='kdp-cover-creator'?'primary':'secondary'} onClick={()=>setForm({...form,coverMode:'kdp-cover-creator'})}>Use KDP Cover Creator</button>
          {form.coverMode==='upload'&&<label className="secondary cursor-pointer"><ImagePlus size={14}/>Choose JPEG<input className="hidden" type="file" accept="image/jpeg" onChange={e=>uploadCover(e.target.files?.[0]||null)}/></label>}
        </div>
        {form.coverUrl&&<div className="hint mt-3">{form.coverWidthPx}×{form.coverHeightPx}px · {(form.coverBytes/1_000_000).toFixed(1)} MB</div>}
      </div>

      <div className="card p-5 mt-5">
        <h3>Pre-Kindle Create confirmations</h3>
        <Toggle label="I reviewed the suggested Guided View panel order/regions" checked={form.guidedViewReviewed} onChange={v=>setForm({...form,guidedViewReviewed:v})}/>
        <Toggle label="I confirmed print and Kindle title/subtitle/author/contributor/series/language/edition metadata match" checked={form.metadataMatchConfirmed} onChange={v=>setForm({...form,metadataMatchConfirmed:v})}/>
      </div>
    </section>

    <section className="card p-6">
      <div className="row items-start"><div><span className="eyebrow">DIGITAL VALIDATION</span><h2>{bundle.validation.status}</h2><p className="muted">{bundle.validation.blockers} blockers · {bundle.validation.warnings} warnings</p></div><a className="secondary" href={bundle.links.kindleCreateGuide} target="_blank"><ExternalLink size={14}/>Kindle Create guide</a></div>
      <div className="mt-5 space-y-2">{bundle.validation.items.map(item=><div className="checkRow" key={item.code}><span className={item.level==='blocker'?'warning':item.level}/><div><b>{item.title}</b><p>{item.detail}</p></div></div>)}</div>

      <button className="primary mt-5" disabled={!!busy||bundle.validation.blockers>0} onClick={build}>{busy==='build'?<LoaderCircle className="spin" size={15}/>:<Download size={15}/>}Build Kindle Create package</button>
      {form.packageUrl&&<a className="secondary mt-5 ml-2" href={form.packageUrl} target="_blank"><Download size={15}/>Download current package</a>}
    </section>

    <section className="card p-6">
      <div className="row items-start"><div><span className="eyebrow">AFTER KINDLE CREATE</span><h2>Preview → KPF → KDP.</h2><p className="muted">The KPF is exported by Kindle Create; Comicrea Studio does not fabricate Amazon’s KPF format.</p></div><MonitorSmartphone/></div>
      <Toggle label="I previewed Guided View in Kindle Create, including Phone / Horizontal" checked={form.kindleCreatePreviewConfirmed} onChange={v=>setForm({...form,kindleCreatePreviewConfirmed:v})}/>
      <Toggle label="I exported the final KPF from Kindle Create" checked={form.kpfExportConfirmed} onChange={v=>setForm({...form,kpfExportConfirmed:v})}/>
      <div className="flex gap-2 flex-wrap mt-4"><button className="secondary" onClick={save}><Save size={14}/>Save confirmations</button><a className="primary" href={bundle.links.bookshelf} target="_blank"><ExternalLink size={14}/>Open KDP Bookshelf</a><button className="secondary" onClick={published}><CheckCircle2 size={14}/>Record published Kindle edition</button></div>
    </section>

    <section className="card p-6">
      <span className="eyebrow">DIGITAL EXPORT HISTORY</span>
      <div className="space-y-2 mt-4">{bundle.history.length?bundle.history.map(item=><div className="softBox" key={item.id}><div className="row"><div><b>{item.kind}</b><div className="text-xs muted">{new Date(item.createdAt).toLocaleString()} · revision {item.digitalRevision}</div></div>{item.url&&<a className="secondary" href={item.url} target="_blank"><Download size={14}/>Open</a>}</div></div>):<p className="muted">No digital exports yet.</p>}</div>
    </section>

    {message&&<div className="hint">{message}</div>}
  </div>
}

function Field({label,children}:{label:string;children:React.ReactNode}){return <label className="field"><span>{label}</span>{children}</label>}
function Toggle({label,checked,onChange}:{label:string;checked:boolean;onChange:(v:boolean)=>void}){return <label className="softBox flex gap-3 items-start cursor-pointer mt-3"><input className="w-auto mt-1" type="checkbox" checked={checked} onChange={e=>onChange(e.target.checked)}/><span>{label}</span></label>}
